/**
 * Application configuration
 * Multi-Environment Support: DEV → PRD deployment strategy
 * Multi-Product Support: WeaveIn (healthcare + M&S) and Triveny.ai (M&S only)
 * Version: 2.1.0 - Multi-product branching via hostname detection
 */

// ============================================================================
// PRODUCT DETECTION
// ============================================================================
// We run a single Vercel build that branches at runtime based on the hostname
// the browser used to load the bundle.
//   - weavein.ai / dev.weavein.ai     → product = 'weavein'  (talks to WeaveIn-api)
//   - app.triveny.ai / dev.triveny.ai → product = 'triveny'  (talks to ms-api)
//
// All product-aware decisions (API base URL, login endpoint, sidebar gating,
// brand assets) should read from `config.product` rather than re-detecting.

export type Product = 'weavein' | 'triveny';

const getProduct = (): Product => {
  // Explicit override via env var (useful for local testing of Triveny mode)
  const envProduct = (import.meta.env.VITE_PRODUCT ?? '').toString().toLowerCase();
  if (envProduct === 'triveny' || envProduct === 'weavein') {
    return envProduct as Product;
  }

  // Hostname-based detection (browser only)
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'app.triveny.ai' || host === 'dev.triveny.ai' || host.endsWith('.triveny.ai') || host === 'triveny.ai') {
      return 'triveny';
    }
  }

  return 'weavein';
};

const product: Product = getProduct();
const isTriveny = product === 'triveny';

// Detect environment based on multiple indicators
const getEnvironment = (): 'development' | 'production' | 'local' => {
  // Explicit environment variable takes precedence
  if (import.meta.env.VITE_ENVIRONMENT === 'production') return 'production';

  // Hostname-based detection — production hosts for either product
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'weavein.ai' || host === 'www.weavein.ai') return 'production';
    if (host === 'dev.weavein.ai') return 'development';
    if (host === 'app.triveny.ai' || host === 'triveny.ai' || host === 'www.triveny.ai') return 'production';
    if (host === 'dev.triveny.ai') return 'development';
  }

  if (import.meta.env.VITE_ENVIRONMENT === 'development') return 'development';

  // Check if running in local dev mode
  if (import.meta.env.DEV) return 'local';

  // Check production build
  if (import.meta.env.PROD) return 'production';

  return 'local';
};

const environment = getEnvironment();

// Debug logging (only in development/local modes)
if (typeof window !== 'undefined' && (environment === 'development' || environment === 'local')) {
  // eslint-disable-next-line no-console
  console.log('🔧 Environment Config:', {
    detected_environment: environment,
    VITE_ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT,
    VITE_WEAVEIN_API_URL: import.meta.env.VITE_WEAVEIN_API_URL,
    VITE_API_URL: import.meta.env.VITE_API_URL,
    VITE_PROVIDER_API_URL: import.meta.env.VITE_PROVIDER_API_URL,
    DEV: import.meta.env.DEV,
    PROD: import.meta.env.PROD,
  });
}

// Environment-specific backend URLs - consolidated to WeaveIn API
// When env var is explicitly '' (Vercel proxy), use '' for relative URLs — ?? preserves this
// When env var is undefined (local/Tauri), fall back to full URLs
const isVercelDevDomain = typeof window !== 'undefined' && window.location.hostname === 'dev.weavein.ai';
const API_URLS = {
  local: (import.meta.env.VITE_API_URL ?? 'http://localhost:8000').trim(),
  development: isVercelDevDomain ? '' : (import.meta.env.VITE_API_URL ?? 'https://trust-marketplace-alov.onrender.com').trim(),
  production: (import.meta.env.VITE_API_URL ?? 'https://weavein-api.onrender.com').trim(),
};

// Use ?? (not ||) so that empty string '' from Vercel env vars is preserved — enabling
// relative URL routing through the Vercel proxy on dev.weavein.ai
// IMPORTANT: On dev.weavein.ai (Vercel), always use relative URLs so requests go through
// vercel.json proxy rewrites, avoiding CORS issues with direct backend calls.
const WEAVEIN_API_URLS = {
  local: (import.meta.env.VITE_WEAVEIN_API_URL || 'http://localhost:8000').trim(),
  development: isVercelDevDomain ? '' : (import.meta.env.VITE_WEAVEIN_API_URL ?? 'https://trust-marketplace-alov.onrender.com').trim(),
  production: (import.meta.env.VITE_WEAVEIN_API_URL ?? 'https://weavein-api.onrender.com').trim(),
};

// Triveny.ai (M&S standalone) API URLs — used when product === 'triveny'.
// These bypass the Vercel proxy and hit ms-api directly. ms-api enforces
// CORS via its own ALLOWED_ORIGINS (includes app.triveny.ai + dev.triveny.ai).
const MS_API_URLS = {
  local: (import.meta.env.VITE_MS_API_URL || 'http://localhost:8000').trim(),
  development: (import.meta.env.VITE_MS_API_URL ?? 'https://ms-api-dev.onrender.com').trim(),
  production: (import.meta.env.VITE_MS_API_URL ?? 'https://ms-api-hots.onrender.com').trim(),
};

// Helper to normalize provider API URL - ensures /api suffix and trims whitespace
const normalizeProviderApiUrl = (url: string): string => {
  const trimmed = url.trim();
  // Strip any trailing /api or /api/ first to normalize, then add /api
  const base = trimmed.replace(/\/api\/?$/, '');
  return `${base}/api`;
};

const PROVIDER_API_URLS = {
  local: normalizeProviderApiUrl(import.meta.env.VITE_PROVIDER_API_URL ?? 'http://localhost:8001/api'),
  development: 'https://provider-api-dev-00es.onrender.com/api',
  // Production provider directory service — the SAME backend the vercel.json
  // /provider-api proxy rewrites to. Used for direct calls (provider search/map
  // via services/providerDirectoryApi.ts, and the desktop app which has no
  // proxy). Its CORS allows both https://weavein.ai and tauri://localhost.
  // (The old `provider-api-dev-00es` host was a stale dev instance.)
  production: 'https://provider-directory-api.onrender.com/api',
};

// Desktop (Tauri) runtime detection — exposed as config.isDesktop so the
// provider-directory admin dashboards skip the Vercel proxy (which the desktop
// webview can't use) and call the absolute service URL directly. The Tauri
// global is injected before the app bundle runs, so this is reliable at
// module-eval time.
const isDesktopRuntime = typeof window !== 'undefined' && '__TAURI__' in window;

// ============================================================================
// PRODUCT-AWARE BACKEND ROUTING
// ============================================================================
// `mainApi.baseUrl` is the primary backend the app talks to. For weavein it
// is weavein-api (full healthcare + M&S). For triveny it is ms-api (M&S only,
// PRODUCT_PROFILE=ms_standalone). Components and services should keep calling
// `config.weaveinApi.baseUrl` — that field is preserved for backward
// compatibility and now resolves to ms-api when running on Triveny.
const mainApiBaseUrl = isTriveny
  ? MS_API_URLS[environment]
  : WEAVEIN_API_URLS[environment];

export const config = {
  // Build information for cache busting
  buildTimestamp: '2025-11-25T12:00:00Z',
  configVersion: '2.1.0',

  // Which product this bundle is rendering as (decides API routing + UI gating)
  product,
  isTriveny,
  isWeavein: !isTriveny,

  // API Configuration - Trust Marketplace (Referrals, Feedback)
  api: {
    baseUrl: import.meta.env.VITE_API_URL ?? API_URLS[environment],
    timeout: 10000, // 10 seconds
  },

  // Main backend API. Routes to ms-api when product === 'triveny',
  // weavein-api otherwise. Field name kept as `weaveinApi` for backward
  // compatibility with the ~60 call sites that reference it.
  weaveinApi: {
    baseUrl: mainApiBaseUrl,
    timeout: 10000, // 10 seconds
  },

  // Provider Directory API Configuration - Provider Directory, Saved Providers
  providerApi: {
    baseUrl: import.meta.env.VITE_PROVIDER_API_URL
      ? normalizeProviderApiUrl(import.meta.env.VITE_PROVIDER_API_URL)
      : PROVIDER_API_URLS[environment],
    timeout: 10000, // 10 seconds
  },

  // App Configuration
  app: {
    name: import.meta.env.VITE_APP_NAME ?? (
      isTriveny
        ? (environment === 'development' ? 'Triveny.ai (DEV)' : 'Triveny.ai')
        : (environment === 'development' ? 'WeaveIn (DEV)' : 'WeaveIn')
    ),
    version: import.meta.env.VITE_APP_VERSION ?? '1.0.0',
    environment,
  },

  // Environment flags
  isDevelopment: environment === 'development',
  isProduction: environment === 'production',
  isLocal: environment === 'local',
  // Desktop (Tauri) build: served from tauri://localhost with NO Vercel proxy,
  // so relative proxy paths like /provider-api/* don't resolve. Callers must
  // use absolute service URLs instead. The Tauri global is injected before the
  // app bundle runs, so this is reliable at module-eval time.
  isDesktop: isDesktopRuntime,
  environment,

  // Support Chat Configuration
  supportChat: {
    enabled: import.meta.env.VITE_ENABLE_SUPPORT_CHAT === 'true',
  },

  // Feedback Widget Configuration
  feedback: {
    enabled: import.meta.env.VITE_ENABLE_FEEDBACK_WIDGET === 'true',
  },
};

// Debug logging for actual URLs being used (development/local only)
if (typeof window !== 'undefined' && (config.isDevelopment || config.isLocal)) {
  // eslint-disable-next-line no-console
  console.log('🌐 Backend URLs:', {
    buildTimestamp: config.buildTimestamp,
    configVersion: config.configVersion,
    product: config.product,
    api_baseUrl: config.api.baseUrl,
    weaveinApi_baseUrl: config.weaveinApi.baseUrl,
    providerApi_baseUrl: config.providerApi.baseUrl,
    app_name: config.app.name,
    environment: config.environment,
  });

  // Debug support widget configuration (development/local only)
  // eslint-disable-next-line no-console
  console.log('🔑 Support Widget Configuration:', {
    supportChatEnabled: config.supportChat.enabled,
    feedbackEnabled: config.feedback.enabled,
  });
}

// Helper function to get full API URL
export const getApiUrl = (endpoint: string) => {
  const baseUrl = config.api.baseUrl.replace(/\/$/, ''); // Remove trailing slash
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${cleanEndpoint}`;
};

// Helper function to get full WeaveIn API URL
// If no endpoint provided, returns just the base URL
export const getWeaveinApiUrl = (endpoint?: string) => {
  const baseUrl = config.weaveinApi.baseUrl?.replace(/\/$/, '') || ''; // Remove trailing slash
  if (!endpoint) return baseUrl;
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${cleanEndpoint}`;
};

// Helper function to check if API is available
export const isApiAvailable = async (): Promise<boolean> => {
  try {
    const response = await fetch(getApiUrl('/health'), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.ok;
  } catch {
    return false;
  }
};
